import { uniqBy } from "lodash-es";
import { createReducer } from "typesafe-actions";

import EmailFolder from "@mapmycustomers/shared/enum/EmailFolder";
import Email from "@mapmycustomers/shared/types/email/Email";
import EmailThread from "@mapmycustomers/shared/types/email/EmailThread";
import EntityEmailSync from "@mapmycustomers/shared/types/email/EntityEmailSync";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";

import {
  Actions,
  closeParentEmailThread,
  destroyEntityEmails,
  dismissEntityEmailsSync,
  fetchEntityEmailSync,
  initEntityEmails,
  loadChildEmails,
  loadParentEmailThreads,
  loadSentEmail,
  openParentEmailThread,
  searchParentEmailThreads,
  setParentEmailThreadsFolder,
} from "./actions";

export interface EntityEmailsState {
  childEmails: Email[];
  childEmailsLoading: boolean;
  entity?: EntitiesSupportedByEmailFeature;
  entityEmailSync?: EntityEmailSync;
  initialized: boolean;
  parentEmailThreadId?: EmailThread["id"];
  parentEmailThreads: EmailThread[];
  parentEmailThreadsFolder: EmailFolder;
  parentEmailThreadsLoading: boolean;
  parentEmailThreadsNeedRefresh: boolean;
  parentEmailThreadsSearching: boolean;
  parentEmailThreadsSearchText: string;
  parentEmailThreadsTotalCount: number;
}

const initialState: EntityEmailsState = {
  childEmails: [],
  childEmailsLoading: false,
  initialized: false,
  parentEmailThreads: [],
  parentEmailThreadsFolder: EmailFolder.INBOX,
  parentEmailThreadsLoading: false,
  parentEmailThreadsNeedRefresh: false,
  parentEmailThreadsSearching: false,
  parentEmailThreadsSearchText: "",
  parentEmailThreadsTotalCount: 0,
};

const entityEmails = createReducer<EntityEmailsState, Actions>(initialState)
  .handleAction(initEntityEmails, (_, { payload: { entity } }) => ({
    ...initialState,
    entity,
    initialized: true,
  }))
  .handleAction(destroyEntityEmails, () => ({
    ...initialState,
  }))
  .handleAction(fetchEntityEmailSync.success, (state, { payload }) => ({
    ...state,
    entityEmailSync: payload,
  }))
  .handleAction(dismissEntityEmailsSync.request, (state) => ({
    ...state,
  }))
  .handleAction(dismissEntityEmailsSync.success, (state) => ({
    ...state,
    entityEmailSync: undefined,
  }))
  .handleAction(setParentEmailThreadsFolder, (state, { payload }) => ({
    ...state,
    parentEmailThreadsFolder: payload,
  }))
  .handleAction(searchParentEmailThreads.request, (state, { payload }) => ({
    ...state,
    parentEmailThreadsSearching: true,
    parentEmailThreadsSearchText: payload?.searchText ?? state.parentEmailThreadsSearchText,
  }))
  .handleAction(searchParentEmailThreads.success, (state, { payload: { data, totalCount } }) => ({
    ...state,
    parentEmailThreads: data,
    parentEmailThreadsNeedRefresh: false,
    parentEmailThreadsSearching: false,
    parentEmailThreadsTotalCount: totalCount,
  }))
  .handleAction(searchParentEmailThreads.failure, (state) => ({
    ...state,
    parentEmailThreadsSearching: false,
  }))
  .handleAction(loadParentEmailThreads.request, (state) => ({
    ...state,
    parentEmailThreadsLoading: true,
  }))
  .handleAction(loadParentEmailThreads.success, (state, { payload: { data, totalCount } }) => ({
    ...state,
    // additional code is required to protect from possible duplicates related to new emails
    // because emails are sorting by date desc
    parentEmailThreads: uniqBy(
      [...state.parentEmailThreads, ...data],
      (emailThread) => emailThread.id
    ),
    parentEmailThreadsLoading: false,
    parentEmailThreadsTotalCount: totalCount,
  }))
  .handleAction(loadParentEmailThreads.failure, (state) => ({
    ...state,
    parentEmailThreadsLoading: false,
  }))
  .handleAction(openParentEmailThread, (state, { payload }) => ({
    ...state,
    childEmails: [],
    parentEmailThreadId: payload,
  }))
  .handleAction(closeParentEmailThread, (state) => ({
    ...state,
    childEmails: [],
    parentEmailThreadId: undefined,
  }))
  .handleAction(loadChildEmails.request, (state) => ({
    ...state,
    childEmailsLoading: true,
  }))
  .handleAction(loadChildEmails.success, (state, { payload }) => ({
    ...state,
    childEmails: payload,
    childEmailsLoading: false,
  }))
  .handleAction(loadChildEmails.failure, (state) => ({
    ...state,
    childEmailsLoading: false,
  }))
  .handleAction(loadSentEmail.request, (state) => ({
    ...state,
    parentEmailThreadsNeedRefresh: true,
  }))
  .handleAction(loadSentEmail.success, (state, { payload }) => ({
    ...state,
    childEmails: [...state.childEmails, payload],
  }));

export type EntityEmailsActions = Actions;

export default entityEmails;
